/**
 * The three lists the entry screen picks from. One hook, three queries, all of
 * them keyed the way the rest of the app keys them so a screen that has
 * already loaded accounts does not load them again.
 *
 * Every URL comes from `apiUrl(domain, …)`. There is no string prefix in this
 * file: which backend serves `categories` is a property of the domain map, and
 * the day it flips this screen does not change.
 */
import { useQuery } from "@tanstack/react-query";
import { apiFetch } from "@/lib/api/client";
import { apiUrl } from "@/lib/api/config";
import type { TopCategory } from "@/lib/suggestions/service";
import type { EntryContext, EntryDraft } from "./draft";
import type { CategoryKind, EntryAccount, EntryCategory, EntryLabel, EntryType } from "./types";

/** `GET /categories` answers with a bare array OR `{ categories: [...] }`. */
function normalizeCategories(json: unknown): EntryCategory[] {
  const rows = Array.isArray(json)
    ? json
    : Array.isArray((json as { categories?: unknown })?.categories)
      ? (json as { categories: unknown[] }).categories
      : [];
  return rows.map((raw) => {
    const c = raw as Partial<EntryCategory>;
    return {
      id: String(c.id ?? ""),
      name: String(c.name ?? ""),
      kind: (c.kind as CategoryKind) ?? "EXPENSE",
      // The whole point of the tolerant shape: a backend that has not grown
      // the hierarchy yet simply has no `parentId`, and that reads as flat.
      parentId: c.parentId ?? null,
      color: c.color ?? null,
      sortOrder: typeof c.sortOrder === "number" ? c.sortOrder : 0,
      isArchived: Boolean(c.isArchived),
    };
  });
}

function normalizeAccounts(json: unknown): EntryAccount[] {
  const rows = Array.isArray(json)
    ? json
    : Array.isArray((json as { accounts?: unknown })?.accounts)
      ? (json as { accounts: unknown[] }).accounts
      : [];
  return rows.map((raw) => {
    const a = raw as Partial<EntryAccount>;
    return {
      id: String(a.id ?? ""),
      bankName: String(a.bankName ?? ""),
      nickname: a.nickname ?? null,
      accountType: a.accountType ?? null,
      color: a.color ?? null,
      isArchived: Boolean(a.isArchived),
    };
  });
}

function normalizeLabels(json: unknown): EntryLabel[] {
  const rows = Array.isArray(json)
    ? json
    : Array.isArray((json as { labels?: unknown })?.labels)
      ? (json as { labels: unknown[] }).labels
      : [];
  return rows.map((raw) => {
    const l = raw as Partial<EntryLabel>;
    return { id: String(l.id ?? ""), name: String(l.name ?? ""), color: l.color ?? null };
  });
}

/**
 * Categories for one kind. Archived rows are fetched (`includeArchived=true`)
 * and filtered at the OFFER boundary rather than at the fetch, so a record
 * already sitting on an archived category can still render — and keep — it.
 * See `selectableCategories`.
 */
export function useCategories(kind: CategoryKind | null) {
  return useQuery({
    queryKey: ["categories", kind],
    enabled: kind !== null,
    queryFn: async (): Promise<EntryCategory[]> => {
      const res = await apiFetch(
        apiUrl("categories", "", { kind: kind as string, includeArchived: "true" }),
      );
      if (!res.ok) return [];
      return normalizeCategories(await res.json());
    },
    staleTime: 60_000,
  });
}

function normalizeTopCategories(json: unknown): TopCategory[] {
  const rows = Array.isArray(json) ? json : [];
  return rows.map((raw) => {
    const t = raw as Partial<TopCategory>;
    return {
      categoryId: String(t.categoryId ?? ""),
      name: String(t.name ?? ""),
      color: t.color ?? null,
      count: typeof t.count === "number" ? t.count : 0,
    };
  });
}

/**
 * The user's top-5 most-used categories for `type`, own-data only — the
 * one-tap chip strip above the category field. Sibling of `useCategories`,
 * fed by `GET /py-api/v1/suggestions/top-categories`
 * (`app/services/suggestion.py::get_top_categories`).
 */
export function useTopCategories(type: "income" | "expense" | null) {
  return useQuery({
    queryKey: ["suggestions", "top-categories", type],
    enabled: type !== null,
    queryFn: async (): Promise<TopCategory[]> => {
      const res = await apiFetch(apiUrl("suggestions", "top-categories", { type: type as string }));
      if (!res.ok) return [];
      return normalizeTopCategories(await res.json());
    },
    staleTime: 60_000,
  });
}

export function useAccounts() {
  return useQuery({
    queryKey: ["accounts", false],
    queryFn: async (): Promise<EntryAccount[]> => {
      const res = await apiFetch(apiUrl("accounts", "", { includeArchived: "false" }));
      if (!res.ok) return [];
      return normalizeAccounts(await res.json());
    },
    staleTime: 60_000,
  });
}

export function useLabels() {
  return useQuery({
    queryKey: ["labels"],
    queryFn: async (): Promise<EntryLabel[]> => {
      const res = await apiFetch(apiUrl("labels"));
      if (!res.ok) return [];
      return normalizeLabels(await res.json());
    },
    staleTime: 60_000,
  });
}

/**
 * The account id to preselect on a fresh entry — the last one used, if it is
 * still selectable. A remembered id that has since been archived (or belongs
 * to another user on a shared browser) renders as "no account" while still
 * being POSTed, so it is reconciled against the live list rather than trusted.
 */
export const LAST_ACCOUNT_KEY = "aim-last-account-id";

export function readLastAccountId(): string {
  try {
    return window.localStorage?.getItem(LAST_ACCOUNT_KEY) ?? "";
  } catch {
    return "";
  }
}

export function rememberAccountId(id: string): void {
  if (!id) return;
  try {
    window.localStorage?.setItem(LAST_ACCOUNT_KEY, id);
  } catch {
    // storage disabled — remembering the account is a nicety, never fatal
  }
}

export function reconcileAccountId(id: string, accounts: EntryAccount[]): string {
  if (!id) return "";
  return accounts.some((a) => a.id === id && !a.isArchived) ? id : "";
}

/**
 * Remember-last-context (per TYPE, never one global slot — see the note on
 * `EntryContext` in `draft.ts`). Same fail-soft posture as the account key
 * above: a private-browsing tab or a full quota degrades to "nothing
 * remembered", never a thrown error.
 */
const LAST_CONTEXT_KEY_PREFIX = "aim-last-context-";

export function readLastContext(type: EntryType): EntryContext | null {
  try {
    const raw = window.localStorage?.getItem(LAST_CONTEXT_KEY_PREFIX + type);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<EntryContext>;
    return {
      categoryId: typeof parsed.categoryId === "string" ? parsed.categoryId : null,
      accountId: typeof parsed.accountId === "string" ? parsed.accountId : "",
      paymentMethod:
        typeof parsed.paymentMethod === "string" ? (parsed.paymentMethod as never) : "UPI",
    };
  } catch {
    return null;
  }
}

export function rememberContext(type: EntryType, ctx: EntryContext): void {
  try {
    window.localStorage?.setItem(LAST_CONTEXT_KEY_PREFIX + type, JSON.stringify(ctx));
  } catch {
    // storage disabled — a nicety, never fatal
  }
}

/**
 * The "Same as last" repeat action. Stores the full draft AS SAVED — this is
 * safe against the `NEVER_CLONED` rule (`draft.ts`) by construction, not by
 * care taken here: `EntryDraft` structurally has no `id` and no
 * `externalSourceId` field to leak, so a straight round-trip through
 * `localStorage` cannot resurrect either one.
 */
const LAST_ENTRY_KEY = "aim-last-entry";

export function readLastEntry(): EntryDraft | null {
  try {
    const raw = window.localStorage?.getItem(LAST_ENTRY_KEY);
    return raw ? (JSON.parse(raw) as EntryDraft) : null;
  } catch {
    return null;
  }
}

export function rememberLastEntry(draft: EntryDraft): void {
  try {
    window.localStorage?.setItem(LAST_ENTRY_KEY, JSON.stringify(draft));
  } catch {
    // storage disabled — a nicety, never fatal
  }
}
